import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Navigation, FileText, MessageCircle } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { DRIVER_HEX } from './LiveMap';
import { shareDriverViaWhatsApp, openDriverPdf } from './DriverSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const assignOrderDriver = async (orderId, driver) => {
  const token = localStorage.getItem('token');
  const res = await axios.post(`${API}/admin/orders/${orderId}/driver`, { driver }, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
};

export const fetchDriverCounts = async () => {
  const token = localStorage.getItem('token');
  const res = await axios.get(`${API}/admin/chauffeur-driver-counts`, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
};

// Knop "Op route" in de bestellijst: kies direct een chauffeur (max per chauffeur).
export const DriverRoutePicker = ({ order, counts, max, onChange, small = false, testid }) => {
  const current = order.assigned_driver;
  const pick = async (driver) => {
    try {
      const res = await assignOrderDriver(order.id, driver);
      onChange(order.id, res.assigned_driver, res.counts);
      toast.success(driver ? `Op route bij ${driver} (${res.counts[driver]}/${max})` : 'Van route gehaald');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mislukt');
    }
  };
  const size = small ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`${size} rounded-md font-medium flex items-center gap-1 ${current ? 'text-white' : 'bg-zinc-100 text-zinc-700 hover:bg-purple-100'}`}
          style={current ? { background: DRIVER_HEX[current] } : undefined}
          data-testid={testid}
        >
          <Navigation className="w-3 h-3" />
          {current ? `${current} ${counts[current] ?? 0}/${max}` : 'Op route'}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="text-xs">Kies chauffeur (max {max})</DropdownMenuLabel>
        {Object.keys(DRIVER_HEX).map((d) => {
          const n = counts[d] ?? 0;
          const full = n >= max && current !== d;
          return (
            <DropdownMenuItem key={d} disabled={full} onSelect={() => pick(d)} data-testid={`${testid}-option-${d}`}>
              <span className="w-2.5 h-2.5 rounded-full mr-2" style={{ background: DRIVER_HEX[d] }} />
              <span className="flex-1">{d}{current === d ? ' ✓' : ''}</span>
              <span className={`ml-3 text-xs ${full ? 'text-red-600 font-semibold' : 'text-zinc-500'}`}>{full ? 'vol' : `${n}/${max}`}</span>
            </DropdownMenuItem>
          );
        })}
        {current && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => pick(null)} className="text-red-600" data-testid={`${testid}-remove`}>
              Van route halen
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Banner bovenaan de bestellijst: per chauffeur aantal + WhatsApp + PDF.
export const DriverRouteBanner = ({ counts, max }) => {
  const token = localStorage.getItem('token');
  const active = Object.entries(counts).filter(([, n]) => n > 0);
  if (active.length === 0) return null;
  return (
    <div className="mb-4 bg-zinc-950 text-white rounded-xl p-4" data-testid="route-banner">
      <div className="flex items-center gap-2 mb-3 text-sm font-semibold">
        <Navigation className="w-5 h-5 text-purple-400" /> Op route per chauffeur
        <span className="text-xs font-normal text-zinc-400">· max {max} per chauffeur</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {active.map(([d, n]) => (
          <div key={d} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3" data-testid={`route-banner-${d}`}>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: DRIVER_HEX[d] }} />
              <span className="font-semibold">{d}</span>
              <span className={`ml-auto text-sm font-bold ${n >= max ? 'text-red-400' : 'text-zinc-300'}`}>{n}/{max}</span>
            </div>
            <div className="flex gap-1.5 mt-2">
              <button onClick={() => shareDriverViaWhatsApp(d, token)} className="flex-1 flex items-center justify-center gap-1 text-xs bg-green-600 hover:bg-green-700 rounded-md py-1.5 font-semibold" data-testid={`route-banner-whatsapp-${d}`}>
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </button>
              <button onClick={() => openDriverPdf(d, token)} className="flex-1 flex items-center justify-center gap-1 text-xs bg-white text-zinc-900 hover:bg-zinc-200 rounded-md py-1.5 font-semibold" data-testid={`route-banner-pdf-${d}`}>
                <FileText className="w-3.5 h-3.5" /> PDF
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
